import { api } from 'src/store/api';
import { type Employee } from '../types/employee';
import { type PaginationResponseAPIMeta, type ResponseWithPaginationMeta } from './types';
import {
  type ActionContractsResponse,
  type InactivateBulkContractsArg,
  type InactivateBulkContractsOriginResponse,
  type GetContractsPayload
} from './contract';

type GetEmployeeByPersonalIdArg = {
  personalId: string;
};

const extendedApi = {
  ...api.injectEndpoints({
    endpoints: build => ({
      getEmployeeByPersonalId: build.query<Employee, GetEmployeeByPersonalIdArg>({
        query: arg => `employee/${arg.personalId}`,
        transformResponse: (response: { data: Employee }) => response.data,
        providesTags: ['Employee']
      }),
      getEmployeesFromContractsDataModel: build.query<ResponseWithPaginationMeta<Employee>, GetContractsPayload>({
        query: data => ({
          url: 'employees',
          method: 'GET',
          params: {
            searchTerm: data.searchValue,
            status: data.filter?.statuses,
            limit: data.pagination.perPage,
            offset: data.pagination.page * data.pagination.perPage
          }
        }),
        transformResponse: (response: { data: Employee[]; meta: PaginationResponseAPIMeta }) => {
          return {
            data: response.data,
            meta: {
              perPage: 0,
              total: response.meta.totalItems,
              lastPage: response.meta.totalPages,
              currentPage: response.meta.currentPage
            }
          };
        },
        providesTags: ['Employee']
      }),
      inactivateEmployeesFromContractsDataModelInBulk: build.mutation<void, { contracts: Employee[] }>({
        query: arg => ({
          url: 'employees/inactivate',
          method: 'POST',
          body: {
            personalIds: arg.contracts.map((contract: Employee) => contract.personalId)
          }
        }),
        invalidatesTags: ['Employee']
      }),
      inactivateEmployeesFromContractsDataModelByFile: build.mutation<
        ActionContractsResponse,
        InactivateBulkContractsArg
      >({
        query: arg => ({
          url: '/employees/inactivate/file',
          method: 'POST',
          headers: {
            'Content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          params: {
            onlyValidate: arg?.onlyValidate
          },
          body: arg.file
        }),
        transformResponse: (response: InactivateBulkContractsOriginResponse) => {
          return { success: response.success, error: [...response?.error, ...response?.notFound] };
        },
        invalidatesTags: ['Employee']
      })
    }),
    overrideExisting: false
  })
};
export const {
  useGetEmployeeByPersonalIdQuery,
  useGetEmployeesFromContractsDataModelQuery,
  useInactivateEmployeesFromContractsDataModelInBulkMutation,
  useInactivateEmployeesFromContractsDataModelByFileMutation
} = extendedApi;
